import { Link } from "react-router-dom";
import { ArrowRight, Play } from "lucide-react";
import { useState } from "react";
import { RichText } from "@/components/RichText";

export const THEMES = {
  light: "bg-white text-slate-800",
  dark: "bg-[var(--site-dark)] text-white",
  accent: "bg-[var(--site-accent)]/15 text-slate-900",
  primary: "bg-[var(--site-primary)] text-white",
};

export const SmartLink = ({ href = "", className, children, testId }) => {
  if (href.startsWith("/") && !href.startsWith("/api/")) return <Link to={href} className={className} data-testid={testId}>{children}</Link>;
  const ext = href.startsWith("http") || href.startsWith("/api/");
  return <a href={href} className={className} target={ext ? "_blank" : undefined} rel={ext ? "noopener noreferrer" : undefined} data-testid={testId}>{children}</a>;
};

export const Buttons = ({ items = [], dark, testId }) => (
  <div className="flex flex-wrap gap-3 mt-6">
    {items.filter((b) => b.label && b.href).map((b, i) => (
      <SmartLink key={i} href={b.href} testId={`${testId}-btn-${i}`}
        className={`inline-flex items-center gap-2 rounded-full px-6 h-11 font-semibold transition-[transform,background-color] duration-200 hover:-translate-y-0.5 ${i === 0 ? "bg-[var(--site-accent)] text-slate-900 hover:brightness-105" : dark ? "border border-white/40 text-white hover:bg-white/10" : "border border-slate-300 text-slate-800 hover:bg-slate-50"}`}>
        {b.label} {i === 0 && <ArrowRight className="w-4 h-4" />}
      </SmartLink>
    ))}
  </div>
);

export const SectionHead = ({ heading, subheading, center = true }) => (heading || subheading) ? (
  <div className={`mb-10 ${center ? "text-center mx-auto" : ""} max-w-3xl`}>
    {heading && <h2 className="text-3xl sm:text-4xl font-extrabold font-['Manrope'] tracking-tight">{heading}</h2>}
    {subheading && <RichText html={subheading} className="mt-3 text-base sm:text-lg opacity-75" />}
  </div>
) : null;

export const Wrap = ({ theme = "light", children, testId, narrow }) => (
  <section className={`${THEMES[theme] || THEMES.light} py-16 sm:py-20`} data-testid={testId}>
    <div className={`mx-auto px-5 sm:px-8 ${narrow ? "max-w-4xl" : "max-w-6xl"}`}>{children}</div>
  </section>
);

export const HeroSection = ({ d, testId }) => {
  const dark = d.theme !== "light";
  return (
    <section className={`relative overflow-hidden ${THEMES[d.theme] || THEMES.dark}`} data-testid={testId}>
      {d.image && <img src={d.image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />}
      <div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-24 sm:py-32">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold font-['Manrope'] tracking-tight max-w-3xl">{d.heading}</h1>
        {d.subheading && <RichText html={d.subheading} className="mt-5 text-lg sm:text-xl opacity-85 max-w-2xl" />}
        <Buttons items={d.buttons} dark={dark} testId={testId} />
      </div>
    </section>
  );
};

export const RichTextSection = ({ d, testId }) => <Wrap theme={d.theme} testId={testId} narrow><RichText html={d.html} className="text-base sm:text-lg leading-relaxed" /></Wrap>;

export const isYouTube = (u = "") => /youtube\.com|youtu\.be/.test(u);
const ytEmbed = (u) => `https://www.youtube.com/embed/${(u.match(/(?:v=|youtu\.be\/|embed\/|shorts\/)([\w-]{11})/) || [])[1] || ""}`;
const vimeoId = (u = "") => (u.match(/vimeo\.com\/(?:video\/)?(\d+)/) || [])[1];
const driveId = (u = "") => (u.match(/drive\.google\.com\/(?:file\/d\/|open\?id=)([\w-]+)/) || [])[1];
export const isEmbeddableVideo = (u = "") => isYouTube(u) || !!vimeoId(u) || !!driveId(u);
const embedUrl = (u) => (isYouTube(u) ? ytEmbed(u) : vimeoId(u) ? `https://player.vimeo.com/video/${vimeoId(u)}` : `https://drive.google.com/file/d/${driveId(u)}/preview`);

export const VideoPlayer = ({ url, poster, testId }) => {
  const [play, setPlay] = useState(false);
  if (isEmbeddableVideo(url)) return <iframe src={embedUrl(url)} title="video" className="w-full aspect-video rounded-2xl" allow="autoplay; fullscreen" allowFullScreen data-testid={testId} />;
  if (!play && poster) return (
    <button onClick={() => setPlay(true)} className="relative w-full group" data-testid={testId}>
      <img src={poster} alt="" className="w-full rounded-2xl object-cover" />
      <span className="absolute inset-0 flex items-center justify-center"><span className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-lg transition-transform group-hover:scale-110"><Play className="w-7 h-7 text-slate-900 ml-1" /></span></span>
    </button>
  );
  return <video src={url} poster={poster} controls autoPlay={play} playsInline preload="metadata" className="w-full rounded-2xl bg-black" data-testid={testId} />;
};

export const ImageTextSection = ({ d, testId }) => (
  <Wrap testId={testId}>
    <div className={`grid md:grid-cols-2 gap-10 items-center ${d.image_side === "left" ? "" : "md:[&>*:first-child]:order-2"}`}>
      <div>{d.video ? <VideoPlayer url={d.video} poster={d.image} testId={`${testId}-video`} /> : d.image && <img src={d.image} alt={d.heading || ""} className="w-full rounded-2xl shadow-xl object-cover" />}</div>
      <div>
        {d.heading && <h2 className="text-3xl sm:text-4xl font-extrabold font-['Manrope'] mb-4">{d.heading}</h2>}
        <RichText html={d.html} className="text-base sm:text-lg leading-relaxed text-slate-600" />
        <Buttons items={d.buttons} testId={testId} />
      </div>
    </div>
  </Wrap>
);

export const CtaSection = ({ d, testId }) => (
  <Wrap theme={d.theme || "accent"} testId={testId} narrow>
    <div className="text-center">
      <h2 className="text-3xl sm:text-4xl font-extrabold font-['Manrope']">{d.heading}</h2>
      {d.text && <RichText html={d.text} className="mt-3 text-lg opacity-80" />}
      <div className="flex justify-center"><Buttons items={d.buttons} dark={d.theme === "dark" || d.theme === "primary"} testId={testId} /></div>
    </div>
  </Wrap>
);

export const VideoSection = ({ d, testId }) => (
  <Wrap testId={testId} narrow>
    {d.heading && <h2 className="text-3xl font-extrabold font-['Manrope'] mb-6 text-center">{d.heading}</h2>}
    {d.url && <VideoPlayer url={d.url} testId={`${testId}-player`} />}
    {d.caption && <p className="mt-3 text-center text-slate-500">{d.caption}</p>}
  </Wrap>
);

export const EmbedSection = ({ d, testId }) => d.url?.startsWith("https://") ? (
  <Wrap testId={testId}><iframe src={d.url} title="embed" className="w-full rounded-2xl border" style={{ height: Number(d.height) || 420 }} /></Wrap>
) : null;
